import React, { useCallback, useRef, useState } from "react";
import { Button, Flex, Link, Text, useCopy } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { type Sandbox } from "../consts";
import { InactiveBanner } from "../InactiveBanner";
import { RecentSandboxes } from "../RecentSandboxes";
import { RemocalStatusIcon } from "../RemocalStatusIcon";
import { StatusHelp } from "../StatusHelp";
import * as widgetStyles from "../styles_vanilla.css";
import { buildSharableSandboxLink } from "../utils";
import * as styles from "./styles_vanilla.css";

const COPIED_FEEDBACK_MS = 2000;

export type MainPanelProps = {
  active: boolean;
  sandboxes: ReadonlyArray<Sandbox> | undefined;
  sandboxesLoading: boolean;
  currentRoutingKey: string | undefined;
  onClose: () => void;
  onOpenPicker: (returnFocus: HTMLElement) => void;
  onSwitch: (routingKey: string) => void;
  onDisconnect: () => void;
  closeButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const MainPanel: React.FC<MainPanelProps> = ({
  active,
  sandboxes,
  sandboxesLoading,
  currentRoutingKey,
  onClose,
  onOpenPicker,
  onSwitch,
  onDisconnect,
  closeButtonRef,
}) => {
  const { copy } = useCopy();
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isBaseline = !currentRoutingKey;
  const currentSandbox = sandboxes?.find((sb) => sb.routingKey === currentRoutingKey);
  const prUrl = currentSandbox?.prUrl;
  const isCurrentSandboxReady = currentSandbox?.ready ?? true;
  const isCurrentSandboxDevbox = currentSandbox?.hasLocalWorkloads ?? false;
  const isCurrentSandboxFork = !isCurrentSandboxDevbox;
  const currentSandboxNameDisplay = currentSandbox?.name || "Baseline";
  const panelTitle = isBaseline
    ? "DEFAULT"
    : isCurrentSandboxDevbox
      ? "DEV SANDBOX"
      : "FORK SANDBOX";
  const isInactive = !isBaseline && !isCurrentSandboxReady;
  const statusDescription = isBaseline
    ? "You're on the shared environment. No sandbox is connected."
    : undefined;

  const handleCopy = useCallback(() => {
    void copy(buildSharableSandboxLink(currentRoutingKey ?? ""));
    setIsCopied(true);
    clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
  }, [copy, currentRoutingKey]);

  return (
    <>
      <Flex flexDirection="column" gap="20px" className={styles.statusCardStyle}>
        <Flex flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex inline alignItems="center" gap={Sizes.SM}>
              <RemocalStatusIcon
                isCurrentSandboxReady={isCurrentSandboxReady}
                isBaseline={isBaseline}
              />
              <Text asSpan variant="upper-sm" className={widgetStyles.sectionLabelStyle}>
                {panelTitle}
              </Text>
              <StatusHelp
                isBaseline={isBaseline}
                isReady={isCurrentSandboxReady}
                isFork={isCurrentSandboxFork}
              />
            </Flex>
            <Button
              ref={active ? closeButtonRef : undefined}
              childAsIcon={{ type: "xmark" }}
              variant="muted"
              aria-label="Close"
              onClick={onClose}
            />
          </Flex>
          <Text variant="h2" className={styles.sandboxNameStyle}>
            {currentSandboxNameDisplay}
          </Text>
          {isBaseline ? (
            <Text variant="secondary-md">{statusDescription}</Text>
          ) : (
            <Flex alignItems="baseline" gap={Sizes.SM}>
              <Text asSpan variant="secondary-md">{`ID: ${currentRoutingKey}`}</Text>
              {prUrl && (
                <Link
                  href={prUrl}
                  shouldOpenInNewTab={true}
                  variant="primary"
                  icon="external-link"
                  iconPosition="right"
                >
                  View PR
                </Link>
              )}
            </Flex>
          )}
          {isInactive && (
            <div className={styles.inactiveBannerStyle}>
              <InactiveBanner />
            </div>
          )}
        </Flex>

        {isBaseline ? (
          <Flex alignSelf="flex-start">
            <Button
              variant="primary"
              icon="link-horizontal"
              isFullWidth={false}
              onClick={(e) => onOpenPicker(e.currentTarget)}
            >
              Connect to a sandbox
            </Button>
          </Flex>
        ) : (
          <Flex justifyContent="space-between" alignItems="center">
            <Flex gap={Sizes.SM}>
              <Button
                variant="neutral"
                icon="repeat"
                onClick={(e) => onOpenPicker(e.currentTarget)}
              >
                Switch
              </Button>
              {isCurrentSandboxFork && (
                <Button
                  icon={isCopied ? "check" : "copy"}
                  iconPosition="left"
                  variant="neutral"
                  aria-label={isCopied ? "Copied" : "Copy shareable link"}
                  onClick={handleCopy}
                >
                  {isCopied ? "Copied" : undefined}
                </Button>
              )}
            </Flex>
            <Button
              variant="dangerSecondary"
              icon="unlink"
              aria-label="Disconnect"
              onClick={(e) => {
                e.preventDefault();
                onDisconnect();
              }}
            />
          </Flex>
        )}
      </Flex>
      <RecentSandboxes
        sandboxes={sandboxes}
        currentRoutingKey={currentRoutingKey}
        sandboxesLoading={sandboxesLoading}
        onSwitch={onSwitch}
      />
    </>
  );
};
